import {
  TAccounts,
  TControllingPersons,
  TOrganizations,
  TTaxResidencies,
  TTaxResidencyAdditionalInformation,
} from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { ukraineOption } from '~/modules/identification/constants.ts';
import {
  controllingPersonStatusMap,
  EMPTY_ACCOUNTS,
  EMPTY_CONTROLLING_PERSONS,
  EMPTY_ORGANIZATIONS,
  EMPTY_TAX_RESIDENCY_JURISDICTIONS,
  showBeneficiaryOwners,
} from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { typeAppeals } from '../AdditionalInformation/constants';
import { EMPTY_OPTION } from '~/components/FormComponents/constants';

export const getTaxResidenciesInitialValues = (
  taxResidencies?: TTaxResidencies[] | null
): TTaxResidencies[] => {
  return (taxResidencies?.length as number) > 0 && taxResidencies
    ? taxResidencies.map((tax) => ({
        jurisdictionCountry: tax?.jurisdictionCountry || null,
        tin: tax?.tin || null,
        tinUnavailableReason: tax?.tinUnavailableReason || null,
        tinUnavailableExplanation: tax?.tinUnavailableExplanation || null,
      }))
    : [EMPTY_TAX_RESIDENCY_JURISDICTIONS];
};

export const getAccountsInitialValues = (organ?: TOrganizations | null): TAccounts[] => {
  return (organ?.accounts?.length as number) > 0 && organ?.accounts
    ? organ.accounts.map((acc) => ({
        iban: acc?.iban || null,
        bankMFO: acc && 'bankMFO' in acc ? (acc.bankMFO ?? null) : 'bankMFO',
        openDate: acc?.openDate || null,
      }))
    : [EMPTY_ACCOUNTS];
};

export const getOrganizationsInitialValues = (
  person?: TControllingPersons | null
): TOrganizations[] => {
  return (person?.organizations?.length as number) > 0 && person?.organizations
    ? person.organizations.map((organ) => ({
        name: organ?.name || null,
        controllingPersonStatus:
          controllingPersonStatusMap.find(
            (item) => item.value === organ?.controllingPersonStatus
          ) || EMPTY_OPTION,
        accounts: getAccountsInitialValues(organ),
      }))
    : [EMPTY_ORGANIZATIONS];
};

export const getInitialValues = (
  formData: TTaxResidencyAdditionalInformation
): TTaxResidencyAdditionalInformation => ({
  organizationName: formData?.organizationName || null,
  registrationJurisdiction: {
    label: (formData?.registrationJurisdiction as string) || ukraineOption.label,
    value: '',
  },
  accountHolderStatus: {
    selectedOption: formData?.accountHolderStatus?.selectedOption || null,
    giin: formData?.accountHolderStatus?.giin || null,
    securitiesMarketName: formData?.accountHolderStatus?.securitiesMarketName || null,
    publicCompanyName: formData?.accountHolderStatus?.publicCompanyName || null,
  },
  taxResidencies: getTaxResidenciesInitialValues(formData?.taxResidencies),
  controllingPersons:
    (formData?.controllingPersons?.length as number) > 0 && formData?.controllingPersons
      ? formData?.controllingPersons?.map((person) => ({
          id: person?.id || null,
          lastName: person?.lastName || null,
          firstName: person?.firstName || null,
          middleName: person?.middleName || null,
          salutation: typeAppeals.find((item) => item.value === person?.salutation) || EMPTY_OPTION,
          isTaxResidentOtherJurisdiction: person?.isTaxResidentOtherJurisdiction ?? null,
          taxResidencies: getTaxResidenciesInitialValues(person?.taxResidencies),
          organizations: getOrganizationsInitialValues(person),
        }))
      : [EMPTY_CONTROLLING_PERSONS],
});

export const mapControllingPersons = (
  controllingPersons: TTaxResidencyAdditionalInformation['controllingPersons']
) => {
  return (
    controllingPersons?.map((person) => {
      const isTaxResidentOtherJurisdiction = person.isTaxResidentOtherJurisdiction;
      const organizationsValue =
        person.organizations?.map((organ) => ({
          ...organ,
          controllingPersonStatus: (organ.controllingPersonStatus as TAutocompleteOption)?.value,
          accounts: organ.accounts?.map(({ bankMFO, ...account }) => account) ?? null,
        })) ?? [];

      return {
        ...person,
        salutation: !isTaxResidentOtherJurisdiction
          ? null
          : (person.salutation as TAutocompleteOption)?.value,
        taxResidencies: !isTaxResidentOtherJurisdiction ? [] : person.taxResidencies,
        organizations: !isTaxResidentOtherJurisdiction ? [] : organizationsValue,
      };
    }) ?? []
  );
};

export const getTaxResidenciesValues = (values: TTaxResidencyAdditionalInformation) => {
  const controllingPersons = mapControllingPersons(values.controllingPersons);

  return {
    ...values,
    accountHolderStatus: {
      ...values?.accountHolderStatus,
      giin:
        values?.accountHolderStatus?.giin === ''
          ? null
          : (values?.accountHolderStatus?.giin ?? null),
    },
    registrationJurisdiction: (values?.registrationJurisdiction as TAutocompleteOption)?.label,
    controllingPersons: !showBeneficiaryOwners.includes(
      values?.accountHolderStatus?.selectedOption!
    )
      ? []
      : controllingPersons,
  };
};
